import { useMemo, useState } from 'react'
import { Notice } from '@shared/ui/Notice'
import { PasswordInput } from '@shared/ui/PasswordInput'
import { useChangeMasterPasswordForm } from '@features/master-password/hooks/useChangeMasterPasswordForm'
import { scorePasswordStrength } from '@features/auth/services/passwordStrength'
import { useTranslation } from '@/i18n'

/**
 * Master password 変更フォーム (Track E-1)。
 *
 *  - inline 展開で SettingsPage の security セクションに描画する。
 *  - 入力検証 (length / mismatch) は flow 側、UI は messageKey を i18n に流すだけ。
 *  - 成功時は `state.kind === 'success'` で完了 Notice を出す。
 *  - 折りたたみは SettingsPage 側のセクションヘッダのトグルで行う
 *    (本セクション内に重複する「閉じる」ボタンは置かない)。
 */

export const ChangeMasterPasswordSection = (): JSX.Element => {
  const { t } = useTranslation()
  const { state, isSubmitting, onSubmit } = useChangeMasterPasswordForm()
  const [newPassword, setNewPassword] = useState('')
  const score = useMemo(() => scorePasswordStrength(newPassword), [newPassword])

  if (state.kind === 'success') {
    return (
      <div className="card settings-security-item" data-testid="change-master-pw-success">
        <Notice tone="info" ico="check">
          {t('settings.security.change_master_pw_success')}
        </Notice>
      </div>
    )
  }

  return (
    <form
      className="card settings-security-item"
      onSubmit={ev => void onSubmit(ev)}
      data-testid="change-master-pw-form"
    >
      <Notice tone="warn" ico="alert">
        {t('settings.security.change_master_pw_warning')}
      </Notice>

      <div className="field">
        <label className="field-label" htmlFor="cmp-old">
          {t('settings.security.change_master_pw_old')}
        </label>
        <PasswordInput id="cmp-old" name="oldPassword" autoComplete="current-password" required />
      </div>

      <div className="field">
        <label className="field-label" htmlFor="cmp-new">
          {t('settings.security.change_master_pw_new')}
        </label>
        <PasswordInput
          id="cmp-new"
          name="newPassword"
          autoComplete="new-password"
          minLength={12}
          required
          value={newPassword}
          onChange={ev => setNewPassword(ev.target.value)}
        />
        <div className="strength-row">
          <div className={`strength s${score}`} aria-hidden="true">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="seg" />
            ))}
          </div>
          <span className="strength-label" aria-live="polite">
            {t('auth.register.strength_label')}: {t(`auth.register.strength_${score}`)}
          </span>
        </div>
        <div className="field-help">{t('auth.register.password_help')}</div>
      </div>

      <div className="field">
        <label className="field-label" htmlFor="cmp-new-confirm">
          {t('settings.security.change_master_pw_new_confirm')}
        </label>
        <PasswordInput
          id="cmp-new-confirm"
          name="newPasswordConfirm"
          autoComplete="new-password"
          minLength={12}
          required
        />
      </div>

      {state.kind === 'error' && (
        <Notice tone="danger" ico="alert">
          {t(state.messageKey)}
        </Notice>
      )}

      <div className="settings-form-actions">
        <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
          {isSubmitting
            ? t('settings.security.change_master_pw_submitting')
            : t('settings.security.change_master_pw_submit')}
        </button>
      </div>
    </form>
  )
}
